import { Button, Divider, Flex, Heading, Spinner, Tooltip } from '@chakra-ui/react';
import { Check, HelpSquare, Mail01, Upload01, XClose } from '@untitled-ui/icons-react';
import { useTranslation } from 'react-i18next';

import { VerificationStatus } from '../types';

type VerificationActionButtonsProps = {
  status: number | undefined;
  isOpenTaskAssignedToCurrentUser: boolean;
  canReplaceInClarificationStep1: boolean;
  canReplaceInClarificationStep2: boolean;
  isInClarificationStep1: boolean;
  isInClarificationStep2: boolean;
  isStep1: boolean;
  onStepOne: boolean;
  onStepTwo: boolean;
  isSystemPartnerFull: boolean;
  shouldDisableSendPoa: boolean;
  isClarificationButtonDisabled: boolean;
  identificationStatus: boolean | undefined;
  businessLicenceStatus: boolean | undefined;
  hasHandwerkskarte: boolean;
  handwerkskarteStatus: boolean | undefined;
  powerOfAttorneyStatus: boolean | undefined;
  isClarificationPending: boolean;
  isPoaPending: boolean;
  isSpfPending: boolean;
  onSendPoaEmail: () => void;
  onSendSpfEmail: () => void;
  onClarification: () => void;
  onReject: () => void;
  onApprove: () => void;
  onSubmitFromClarification: () => void;
  hasStepOneErrors: boolean;
  hasStepTwoErrors: boolean;
  canManuallyUploadPoa: boolean;
  isManualPoaUploadAllowed: boolean;
  onManuallyUploadPoa: () => void;
};

const VerificationActionButtons = ({
  status,
  isOpenTaskAssignedToCurrentUser,
  canReplaceInClarificationStep1,
  canReplaceInClarificationStep2,
  isInClarificationStep1,
  isInClarificationStep2,
  isStep1,
  onStepOne,
  onStepTwo,
  isSystemPartnerFull,
  shouldDisableSendPoa,
  isClarificationButtonDisabled,
  identificationStatus,
  businessLicenceStatus,
  hasHandwerkskarte,
  handwerkskarteStatus,
  powerOfAttorneyStatus,
  isClarificationPending,
  isPoaPending,
  isSpfPending,
  onSendPoaEmail,
  onSendSpfEmail,
  onClarification,
  onReject,
  onApprove,
  onSubmitFromClarification,
  hasStepOneErrors,
  hasStepTwoErrors,
  canManuallyUploadPoa,
  isManualPoaUploadAllowed,
  onManuallyUploadPoa,
}: VerificationActionButtonsProps) => {
  const { t } = useTranslation();

  const isClarificationReplacementUser = canReplaceInClarificationStep1 || canReplaceInClarificationStep2;

  const manualPoaUploadDisabledReason = !canManuallyUploadPoa
    ? t('workshop_verification_request:manual_poa_upload.tooltip.no_permission')
    : !isManualPoaUploadAllowed
      ? t('workshop_verification_request:manual_poa_upload.tooltip.wrong_status')
      : null;

  return (
    <>
      <Divider my={5} />
      <Heading as="h3" size="xxs" pb={6}>
        {t('workshop_verification_request:your_actions')}
      </Heading>
      <Flex flexDirection="column" w="100%">
        {isClarificationReplacementUser && (isInClarificationStep1 || isInClarificationStep2) && (
          <Flex justifyContent="flex-end" mb={2}>
            <Button disabled={(isStep1 && hasStepOneErrors) || (!isStep1 && hasStepTwoErrors)} variant="primary" onClick={onSubmitFromClarification}>
              {t('workshop_verification_request:button.submit_for_verification', 'Submit for verification')}
            </Button>
          </Flex>
        )}
        <Flex flexWrap="wrap" justifyContent="flex-end">
          <Tooltip label={manualPoaUploadDisabledReason} hasArrow shouldWrapChildren>
            <Button
              colorScheme="blue"
              variant="primary"
              rightIcon={<Upload01 />}
              m={1}
              disabled={manualPoaUploadDisabledReason != null}
              onClick={onManuallyUploadPoa}
            >
              {t('workshop_verification_request:button.manually_upload_poa')}
            </Button>
          </Tooltip>

          <Button
            colorScheme="blue"
            variant="primary"
            rightIcon={isPoaPending ? <Spinner size="md" thickness="2px" speed="0.65s" emptyColor="gray.200" color="blue.500" /> : <Mail01 />}
            m={1}
            disabled={isClarificationReplacementUser || !isOpenTaskAssignedToCurrentUser || shouldDisableSendPoa}
            onClick={onSendPoaEmail}
          >
            {t('workshop_verification_request:button.poa_email')}
          </Button>

          <Button
            variant="primary"
            rightIcon={isSpfPending ? <Spinner size="md" thickness="2px" speed="0.65s" emptyColor="gray.200" color="blue.500" /> : <Mail01 />}
            m={1}
            disabled={isClarificationReplacementUser || !isSystemPartnerFull}
            onClick={onSendSpfEmail}
          >
            {t('workshop_verification_request:button.send_email')}
          </Button>

          <Button
            colorScheme="orange"
            rightIcon={<HelpSquare />}
            isLoading={isClarificationPending}
            m={1}
            disabled={isClarificationReplacementUser || isClarificationButtonDisabled}
            onClick={onClarification}
          >
            {t('workshop_verification_request:button.clarification')}
          </Button>

          <Button
            colorScheme="red"
            rightIcon={<XClose />}
            m={1}
            disabled={isClarificationReplacementUser || !isOpenTaskAssignedToCurrentUser}
            onClick={onReject}
          >
            {t('workshop_verification_request:reject_verification.modal.header')}
          </Button>

          <Button
            colorScheme="green"
            rightIcon={<Check />}
            m={1}
            onClick={onApprove}
            disabled={
              isClarificationReplacementUser ||
              status === VerificationStatus.CLARIFICATION_STEP_2 ||
              status === VerificationStatus.CLARIFICATION_STEP_1 ||
              status === VerificationStatus.WAITING_ON_POA ||
              !isOpenTaskAssignedToCurrentUser ||
              status === VerificationStatus.COMPLETED ||
              (onStepOne && !(identificationStatus && businessLicenceStatus)) ||
              (onStepOne && hasHandwerkskarte && !handwerkskarteStatus) ||
              (onStepTwo && !powerOfAttorneyStatus)
            }
          >
            {onStepOne ? t('workshop_verification_request:button.approve') : t('workshop_verification_request:button.complete')}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default VerificationActionButtons;
